import { useState } from 'react';
import AnnouncementBar from '../home/components/AnnouncementBar';
import Navbar from '../home/components/Navbar';
import FooterSection from '../home/components/FooterSection';
import SEOHead from '../../components/feature/SEOHead';
import { SITE_URL } from '../../utils/seo';

const advantages = [
  { icon: 'ri-infinity-line', title: 'Sınırsız Anlık Görüntü', desc: 'Performans etkisi olmadan istediğiniz sıklıkta anlık görüntü alın ve sonsuza kadar saklayın.' },
  { icon: 'ri-layout-grid-line', title: 'Granüler Kapsam', desc: 'Tek bir VM\'yi veya tüm veri merkezini anlık görüntüye alın — aynı teknoloji, aynı verimlilik.' },
  { icon: 'ri-server-line', title: 'Emtia Donanımı', desc: 'Pahalı özel depolama donanımı gerektirmez; standart sunucularda çalışır.' },
  { icon: 'ri-shield-star-line', title: 'VM Koruması', desc: '"Stun-less" VM koruması — VM\'ler klon oluşturma sırasında etkilenmez.' },
];

const useCases = [
  { title: 'Yedekleme', desc: 'Anlık görüntü tabanlı, performans etkisi olmayan sürekli yedekleme.' },
  { title: 'Felaket Kurtarma', desc: 'Bağımsız kopyalar ile hızlı site kurtarma ve yük devri.' },
  { title: 'Test & Geliştirme', desc: 'Anlık ortam klonları ile test/dev için gerçekçi replikalar.' },
  { title: 'Altın Kopya', desc: 'Aynı uygulamanın yüzlerce örneğini milisaniyeler içinde oluşturun.' },
];

const faqList = [
  { q: 'ioClone geleneksel anlık görüntülerden nasıl farklıdır?', a: 'Geleneksel anlık görüntüler bir önceki anlık görüntüye bağımlıdır ve b-ağacı yapısı nedeniyle zamanla yavaşlar. ioClone anlık görüntüleri bağımsız, bağımsız kopyalardır. Bir önceki anlık görüntüye bağımlılık yoktur; bu da 3-2-1 yedekleme kuralını karşılamalarını sağlar.' },
  { q: 'ioClone ne kadar hızlıdır?', a: 'ioClone, milisaniyeler içinde kopyalar oluşturur ve başlangıçta sıfır ek kapasite tüketir. Global Satır İçi Tekilleştirme sayesinde kopyalar yalnızca benzersiz değişiklikleri depolar.' },
  { q: 'ioClone ile kaç anlık görüntü alabilirsiniz?', a: 'Pratik olarak sınırsız. ioClone anlık görüntüleri performansa etkisi olmadan sonsuza kadar saklanabilir, bu da uzun süreli saklama politikalarını mümkün kılar.' },
  { q: 'ioClone fidye yazılımına karşı nasıl koruma sağlar?', a: 'ioClone anlık görüntüleri değişmezdir; yani fidye yazılımı tarafından değiştirilemez. Bu, enfeksiyondan önceki herhangi bir noktaya geri dönmeyi mümkün kılar.' },
  { q: 'ioClone ile tüm veri merkezini klonlayabilir misiniz?', a: 'Evet. ioClone, tek VM\'den tüm sanal veri merkezine (VDC) kadar herhangi bir şeyin klonlanmasını destekler. Büyüklük fark etmeksizin milisaniyeler içinde gerçekleşir.' },
];

const iocloneJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'ioClone',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Any',
  description: 'ioClone ile anlık sanal makine ve VDC klonlama. Sıfır gecikme ile test ortamları ve üretim ortamı kopyaları.',
  url: `${SITE_URL}/ioclone`,
};

const iocloneFaqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'ioClone geleneksel anlık görüntülerden nasıl farklıdır?', acceptedAnswer: { '@type': 'Answer', text: "Geleneksel anlık görüntüler bir önceki anlık görüntüye bağımlıdır ve b-ağacı yapısı nedeniyle zamanla yavaşlar. ioClone anlık görüntüleri bağımsız, bağımsız kopyalardır; 3-2-1 yedekleme kuralını karşılar." } },
    { '@type': 'Question', name: 'ioClone ne kadar hızlıdır?', acceptedAnswer: { '@type': 'Answer', text: 'ioClone, milisaniyeler içinde kopyalar oluşturur ve başlangıçta sıfır ek kapasite tüketir. Global Satır İçi Tekilleştirme sayesinde kopyalar yalnızca benzersiz değişiklikleri depolar.' } },
    { '@type': 'Question', name: 'ioClone ile kaç anlık görüntü alabilirsiniz?', acceptedAnswer: { '@type': 'Answer', text: 'Pratik olarak sınırsız. ioClone anlık görüntüleri performansa etkisi olmadan sonsuza kadar saklanabilir.' } },
    { '@type': 'Question', name: 'ioClone fidye yazılımına karşı nasıl koruma sağlar?', acceptedAnswer: { '@type': 'Answer', text: 'ioClone anlık görüntüleri değişmezdir; yani fidye yazılımı tarafından değiştirilemez. Bu, enfeksiyondan önceki herhangi bir noktaya geri dönmeyi mümkün kılar.' } },
    { '@type': 'Question', name: 'ioClone ile tüm veri merkezini klonlayabilir misiniz?', acceptedAnswer: { '@type': 'Answer', text: "Evet. ioClone, tek VM'den tüm sanal veri merkezine (VDC) kadar herhangi bir şeyin klonlanmasını destekler. Büyüklük fark etmeksizin milisaniyeler içinde gerçekleşir." } },
  ],
};

export default function IoClonePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen w-full bg-white">
      <SEOHead
        title="ioClone | Anlık VM ve VDC Klonlama | VergeOS"
        description="ioClone ile anlık sanal makine ve sanal veri merkezi klonlama. Test ortamları için sıfır gecikme kopyalar, üretim ortamı yansıması."
        keywords="ioClone, VM klonlama, VDC klonlama, anlık klonlama, VergeOS, test ortamı"
        canonicalPath="/ioclone"
        jsonLd={[iocloneJsonLd, iocloneFaqJsonLd]}
      />
      <AnnouncementBar />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full overflow-hidden bg-[#0f1117] py-24 text-center">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'repeating-linear-gradient(45deg,#e8600a 0,#e8600a 1px,transparent 0,transparent 50%)', backgroundSize: '20px 20px' }} />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f1117]/60 to-[#0f1117]/90" />
          <div className="relative z-10 max-w-4xl mx-auto px-6">
            <div className="inline-block bg-[#e8600a]/20 text-[#e8600a] text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">Özellikler</div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              <span className="text-[#e8600a]">ioClone</span> ile VM Snapshot Sorununu Çözün
            </h1>
            <p className="text-xl text-gray-300 mb-4 font-medium">Sınırsız Snapshot — Sıfır Performans Etkisi</p>
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {['Sınırsız Anlık Görüntü — Performans Etkisi Yok', 'Granüler — VM veya Tüm Veri Merkezi', 'Emtia Sunucuları Kullanır', '"Stun-less" VM Koruması'].map(item => (
                <span key={item} className="bg-white/10 text-white text-xs px-3 py-1 rounded-full">{item}</span>
              ))}
            </div>
          </div>
        </section>

        {/* Problem */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-6">Eski Anlık Görüntülerin Sorunlarını Aşın</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">Diğer hiper yönetici dosya sistemleri, yazılım tanımlı depolama ve özel depolama donanımı tarafından kullanılan eski snapshot teknolojisi karmaşık b-ağacı yapılarına bağımlıdır. Bu anlık görüntülerin verimsizliği müşterileri şunları yapmaya zorlar:</p>
                <ul className="space-y-3 mb-6">
                  {['Aktif anlık görüntü sayısını sınırlamak', 'Anlık görüntüleri yedekleme uygulamalarına hızlıca boşaltmak', 'Depolama işlem ve performansı için fazla ödeme yapmak', 'Anlık görüntüler yerine yedeklemelere güvenmek'].map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-close-circle-line text-red-400 text-base shrink-0 mt-0.5"></i>{item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img src="https://public.readdy.ai/ai/img_res/b96495e5f20885610bd3616988907dfc.jpg" alt="ioClone Diyagramı" className="w-full h-full object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="py-20 bg-[#f8fafc]">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-[#1e293b] text-center mb-12">ioClone'un Avantajları</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {advantages.map(a => (
                <div key={a.title} className="bg-white rounded-lg border border-gray-100 p-6 text-center">
                  <span className="w-12 h-12 flex items-center justify-center bg-[#e8600a]/10 rounded-lg text-[#e8600a] mx-auto mb-4"><i className={`${a.icon} text-2xl`}></i></span>
                  <h3 className="font-semibold text-[#1e293b] mb-2 text-sm">{a.title}</h3>
                  <p className="text-xs text-gray-600 leading-relaxed">{a.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Next Gen Copies */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="rounded-xl overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=ioClone%20golden%20master%20concept%20showing%20one%20source%20VM%20being%20cloned%20into%20hundreds%20of%20instances%20for%20backup%20DR%20testing%20and%20development%20environments%20simultaneously%2C%20blockchain-like%20deduplication%20technology%20space-efficient%20copies%20visualization%20dark%20background%20professional%20technical%20diagram&width=580&height=400&seq=ioclone_gen1&orientation=landscape" alt="ioClone Yeni Nesil Kopyalar" className="w-full h-full object-top" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-6">Yeni Nesil Veri Kopyaları</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">ioClone, UltraConverged Altyapımız (UCI) olan VergeOS'a entegre edilmiştir. Dosya sistemi VergeFS, alan verimli VM, birim veya tüm veri merkezi kopyaları sağlamak için blok zinciri benzeri teknolojiyi kullanır.</p>
                <p className="text-gray-600 mb-6 leading-relaxed">VergeOS'un global tekilleştirme yetenekleri, VergeOS klonlarında kopyaların milisaniyeler içinde oluşturulduğu ve başlangıçta sıfır ek kapasite tükettiği anlamına gelir.</p>
                <div className="grid grid-cols-2 gap-4">
                  {useCases.map(u => (
                    <div key={u.title} className="bg-[#f8fafc] rounded-lg p-4 border border-gray-100">
                      <p className="font-semibold text-[#1e293b] text-sm mb-1">{u.title}</p>
                      <p className="text-xs text-gray-600">{u.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Reusable Forever */}
        <section className="py-20 bg-[#0f1117]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Sonsuza Kadar Yeniden Kullanılabilir</h2>
            <p className="text-gray-400 mb-8 leading-relaxed text-sm">ioClone, BT\'nin test-dev, QA, raporlama ve benzer uygulamalar için alan verimli kopyaları yeniden kullanmasını sağlar. Aynı uygulamanın birden fazla örneğini oluşturmanız mı gerekiyor? Milisaniyeler içinde yüzlerce yeniden yazılabilir, alan verimli klon oluşturun — performans etkisi olmaksızın ve sonsuza kadar kullanabilirsiniz.</p>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-white border-t border-gray-200">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-[#1e293b] text-center mb-12">ioClone SSS</h2>
            <div className="space-y-4">
              {faqList.map((faq, i) => (
                <div key={i} className="bg-gray-50 rounded-lg border border-gray-100 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer">
                    <span className="font-medium text-[#1e293b] text-sm">{faq.q}</span>
                    <span className="w-5 h-5 flex items-center justify-center text-gray-400 shrink-0"><i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg`}></i></span>
                  </button>
                  {openFaq === i && <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-4">{faq.a}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}
